import * as React from 'react';
import { LinkProps } from 'react-router-dom';
import { FeatherIconName } from './FeatherIconName';
import { FeatherIcon } from './FeatherIcon';
import { InternalLink } from './InternalLink';

export type ButtonKind = 'primary' | 'plain' | 'plain-inverted' | 'destructive';

interface ButtonProps {
  icon?: FeatherIconName;
  kind?: ButtonKind;
  layoutClass?: string;
}

export const buttonClass =
  'font-medium px-8 py-3 rounded-lg border text-center transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm';

const buttonFocusClass =
  'focus:ring-2 focus:ring-primary-300 focus:ring-opacity-60 focus:outline-none';

const buttonColors = (kind: ButtonKind) => {
  switch (kind) {
    case 'primary':
      return `bg-primary hover:bg-primary-400 text-white border-primary hover:border-primary-400 ${buttonFocusClass}`;
    case 'plain-inverted':
      return 'bg-transparent hover:bg-darken-15 text-white';
    case 'plain':
      return `bg-white text-primary border-primary hover:bg-primary-100 ${buttonFocusClass}`;
    case 'destructive':
      return 'text-red-700 hover:bg-red-100 focus:ring-2 focus:ring-red-300 focus:ring-opacity-60 focus:outline-none';
  }
};

export const Button: React.FC<ButtonProps & React.ComponentProps<'button'>> = ({
  className = '',
  kind = 'primary',
  children,
  icon,
  layoutClass = 'block w-full',
  ...props
}) => {
  return (
    <button
      {...props}
      className={`${buttonClass} ${layoutClass} ${buttonColors(
        kind,
      )} ${className}`}
    >
      <ButtonContent icon={icon}>{children}</ButtonContent>
    </button>
  );
};

export const ButtonLink: React.FC<ButtonProps & LinkProps> = ({
  className = '',
  children,
  icon,
  kind = 'primary',
  layoutClass = 'block w-full',
  ...props
}) => {
  return (
    <InternalLink
      {...props}
      className={`${buttonClass} ${layoutClass} ${buttonColors(
        kind,
      )} ${className}`}
    >
      <ButtonContent icon={icon}>{children}</ButtonContent>
    </InternalLink>
  );
};

export const ExternalButtonLink: React.FC<
  ButtonProps & React.ComponentProps<'a'>
> = ({
  className = '',
  children,
  icon,
  kind = 'primary',
  layoutClass = 'block w-full',
  ...props
}) => (
  <a
    {...props}
    className={`${buttonClass} ${layoutClass} ${buttonColors(
      kind,
    )} ${className}`}
  >
    <ButtonContent icon={icon}>{children}</ButtonContent>
  </a>
);

interface ButtonContentProps {
  children: React.ReactNode;
  icon?: FeatherIconName;
}

export const ButtonContent: React.FC<ButtonContentProps> = ({
  children,
  icon,
}) => {
  return (
    <div className="h-full flex justify-between items-center">
      {icon && <div className="w-6" />}
      <div className="flex-1">{children}</div>
      {icon && (
        <div className="w-6">
          <FeatherIcon icon={icon} />
        </div>
      )}
    </div>
  );
};
